/*
 * Copyright (c) Nexign, JSC, 2022-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.19.0
 */

import { useMemo, useState } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { useDispatch, useSelector } from 'react-redux';

import { LinkBlock } from '@components/common';
import { PagePanelButtonSelectStyled } from '@components/common/PageTitle/styled/PageTitleStyled';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { TInstructionLink } from '@modules/internationalization/types/corporate';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectFeatureEnabled } from '@pages/PageAuth/selectors/authSelectors';

import Select, { IOptionItemProps } from '../../../../components/common/Select/Select';
import { API_HOSTNAME, API_VERSION_DEFAULT } from '../../../app/Api';
import { makeSelectTranslationCommon } from '../../../internationalization/intSelectors';
import { modalOpen } from '../../../modal/modalActions';
import { IModalRenderProps } from '../../../modal/types/modalTypes';
import TableExportModal from './TableExportModal';
import { useTableExport } from './useTableExport';


interface ITableExport {
    disabled?: boolean;
    options?: string[];
    url: string;
    instructionLink?: TInstructionLink;
}

const TableExport = (
    {
        disabled,
        options: optionProps,
        url,
        instructionLink,
    }: ITableExport) => {
    const dispatch = useDispatch();

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const isInstructionsLinksEnabled = useSelector(makeSelectFeatureEnabled(FeatureNameEnum.INSTRUCTIONS_LINKS));

    const { options: optionFromTable } = useTableExport();
    const [tooltipLink, setTooltipLink] = useState(instructionLink?.instructionLink);

    const options = optionProps || optionFromTable;

    const TooltipMessage = useMemo(() => {
       if (tooltipLink && instructionLink) {
           return (
               <>
                   <Typography>
                       {instructionLink?.tooltipText}
                   </Typography>
                   <LinkBlock
                       isExternalUrl
                       target={'_blank'}
                       url={instructionLink?.instructionLink}
                   >
                       {instructionLink?.linkText}
                   </LinkBlock>
               </>
           )
       } else {
           return undefined
       }
    },[tooltipLink, instructionLink])

    const onSelectHandler = (item: IOptionItemProps) => {
        dispatch(modalOpen({
            render: (p: IModalRenderProps) => <TableExportModal
                handleClose={p.onCloseModal}
                handleSubmit={() => {
                    p.onCloseModal();
                    window.open(`${API_HOSTNAME}/${API_VERSION_DEFAULT}/${url}/${item.value}`, '_blank');
                }}
            />,
        }));
    };
    return (
        <PagePanelButtonSelectStyled>
            <ArrowTooltip
                disableHoverListener={
                    !isInstructionsLinksEnabled &&
                    !tooltipLink
                }
                message={TooltipMessage}
                placement={'top'}
            >
                <div
                    onClick={() => setTooltipLink('')}
                    onMouseLeave={() => setTooltipLink(instructionLink?.instructionLink)}
                >
                    <Select
                        disabled={disabled}
                        kind="normal"
                        options={options.map(option => ({ label: option, value: option }))}
                        placeholder={translationCommon.export.export}
                        selectedOption={{ label: '', value: '' }}
                        translation={translationCommon}
                        onSelect={onSelectHandler}
                    />
                </div>
            </ArrowTooltip>
        </PagePanelButtonSelectStyled>
    );
};

export default TableExport;
